import type {
  DecisionStatusEnum,
  ExecutionReport,
  IOMSNewOrderSingle4200Aggregations,
  OMSOrderPropertyEnum,
  PricingMode,
  SideEnum,
} from '@talos/kyoko';
import type { FullOrderRequest } from '../../../providers/orders.types';

/** Bin data with ISO date strings */
export interface PreTradeBin {
  StartTime: string;
  EndTime: string;
  Volume: string;
  StartTargetQty: string;
  EndTargetQty: string;
  StartUpperBound: string;
  EndUpperBound: string;
  StartLowerBound: string;
  EndLowerBound: string;
  BinQty: string;
}

export interface PreTradeAnalytic {
  Property: OMSOrderPropertyEnum;
  Value: string;
  /** Optional market name when analytic is market-specific */
  Market?: string;
  /** Optional symbol when multiple symbols exist per market (unified liquidity) */
  Symbol?: string;
}

/** Fields from the NOS that were considered in the pre-trade analysis */
export interface PreTradeNOSConsideredField {
  Name: string;
}

/**
 * Market item inside `NewOrderSingle.Markets`.
 * This is the richer request/order payload shape used for resubmit priming.
 */
export interface PreTradeNOSMarket {
  Market?: string;
  MarketAccount?: string;
  LegIndex?: number;
  Symbol?: string;
  ParentSymbol?: string;
}

/**
 * Request-shaped NewOrderSingle payload used for pre-trade submit/compare flows.
 */
export type PreTradeRequestNewOrderSingle = Omit<FullOrderRequest, 'Markets'> & {
  Side: SideEnum | string;
  PricingMode?: PricingMode;
  InitialDecisionStatus?: DecisionStatusEnum;
  StartTime?: string;
  EndTime?: string;
  Aggregations?: IOMSNewOrderSingle4200Aggregations[];
  Markets: PreTradeNOSMarket[];
};

/**
 * Scenario/inbound NewOrderSingle from pre-trade responses.
 * It is request-shaped and may optionally include a small subset of execution-style fields.
 */
export type PreTradeScenarioNewOrderSingle = PreTradeRequestNewOrderSingle &
  Partial<Pick<ExecutionReport, 'DecisionStatus' | 'OrdStatus' | 'CumQty' | 'LegSummary' | 'MultilegParams'>>;

/** Pre-trade request containing SessionID and Order */
export interface PreTradeRequest {
  SessionID: string;
  /**
   * New-order request payload produced by OrderService.generateRequest.
   * This is request-shaped (markets as string[]), not a hydrated Order entity.
   */
  Order: Omit<PreTradeRequestNewOrderSingle, 'Markets'> & { Markets: string[] };
}

/** API response data */
export interface PreTradeResponseData {
  OrgID?: number;
  Timestamp: string;
  User: string;
  RequestedTimestamp: string;
  RequestID: string;
  Symbol: string;
  Strategy: string;
  StartTime: string;
  EndTime: string;
  Quantity: string;
  Status: string;
  BoundsControl?: number;
  RequestedAlgoType: string;
  ResponseAlgoType: string;
  Currency?: string;
  Bins: PreTradeBin[];
  Analytics: PreTradeAnalytic[];
  NOSConsideredFields: PreTradeNOSConsideredField[];
  /** Scenario-level market summary; not the same as `NewOrderSingle.Markets`. */
  Markets: Array<{ Market?: string; Symbol?: string }>;
  NewOrderSingle: PreTradeScenarioNewOrderSingle;
  SessionIdentifier: string;
  Text?: string;
}

export interface PreTradeResponse {
  type: 'PreTradeResponse';
  ts: string;
  data: PreTradeResponseData[];
}

// Compatibility aliases used by existing imports/tests.
export type PreTradeNewOrderSingle = PreTradeRequestNewOrderSingle;
export type PreTradeMarket = PreTradeNOSMarket;
export type PreTradeOrderRequest = FullOrderRequest;

/** Query params for pre-trade sessions/data endpoint. */
export interface PreTradeDataParams {
  [key: string]: string | undefined;
  Symbol?: string;
  Strategy?: string;
  StartDate?: string;
  EndDate?: string;
  SessionID?: string;
  RequestID?: string;
  Limit?: string;
}

// Analytics property constants
export const PreTradeAnalyticProperty = {
  SpreadCost: 'SpreadCost',
  TemporaryCost: 'TemporaryCost',
  TimeRisk: 'TimeRisk',
} as const;

export type PreTradeAnalyticProperty = (typeof PreTradeAnalyticProperty)[keyof typeof PreTradeAnalyticProperty];
